import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
    Box,
    Button,
    Chip,
    FormControlLabel,
    IconButton,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { BRAND_CORAL, BRAND_CORAL_WASH, emptyContact } from '../../constants'
import { VendorOnboardingForm } from '../../interfaces'
import { validateEmail } from '../../validators'
import PhoneField from '../PhoneField'
import { FieldGrid, StageHeading, textFieldSx } from '../StepChrome'

const ContactsStep = () => {
    const { control } = useFormContext<VendorOnboardingForm>()
    const { fields, append, remove } = useFieldArray({ control, name: 'contacts' })

    return (
        <Box>
            <StageHeading
                title="Contact persons"
                blurb="Add as many people as you need — there's no limit. Contact 1 is the primary contact person: it's the contact created as primary in Zoho Books and shown on invoices, so add that person first."
            />

            <Stack spacing={3}>
                {fields.map((field, index) => {
                    const isFirst = index === 0
                    return (
                        <Box
                            key={field.id}
                            sx={{
                                p: 2.5,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                backgroundColor: '#fff',
                            }}
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{ mb: 2 }}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    flexWrap="wrap"
                                >
                                    <Typography sx={{ fontWeight: 700 }}>
                                        Contact {index + 1}
                                    </Typography>
                                    {isFirst && (
                                        <>
                                            <Chip
                                                size="small"
                                                label="Primary contact"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: BRAND_CORAL,
                                                    backgroundColor:
                                                        BRAND_CORAL_WASH,
                                                    border: '1px solid',
                                                    borderColor: BRAND_CORAL,
                                                }}
                                            />
                                            <Typography
                                                variant="caption"
                                                sx={{ color: 'text.secondary' }}
                                            >
                                                These fields are required
                                            </Typography>
                                        </>
                                    )}
                                </Stack>

                                {!isFirst && (
                                    <Tooltip title="Remove this contact">
                                        <IconButton
                                            onClick={() => remove(index)}
                                            size="small"
                                            aria-label={`Remove contact ${index + 1}`}
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Stack>

                            <FieldGrid>
                                <Controller
                                    control={control}
                                    name={`contacts.${index}.firstName` as const}
                                    rules={
                                        isFirst
                                            ? { required: 'First name is required' }
                                            : {}
                                    }
                                    render={({ field: f, fieldState }) => (
                                        <TextField
                                            {...f}
                                            label={`First name${isFirst ? ' *' : ''}`}
                                            fullWidth
                                            sx={textFieldSx}
                                            error={Boolean(fieldState.error)}
                                            helperText={fieldState.error?.message}
                                        />
                                    )}
                                />
                                <Controller
                                    control={control}
                                    name={`contacts.${index}.lastName` as const}
                                    rules={
                                        isFirst
                                            ? { required: 'Last name is required' }
                                            : {}
                                    }
                                    render={({ field: f, fieldState }) => (
                                        <TextField
                                            {...f}
                                            label={`Last name${isFirst ? ' *' : ''}`}
                                            fullWidth
                                            sx={textFieldSx}
                                            error={Boolean(fieldState.error)}
                                            helperText={fieldState.error?.message}
                                        />
                                    )}
                                />
                                <Controller
                                    control={control}
                                    name={`contacts.${index}.email` as const}
                                    rules={{
                                        validate: (value) =>
                                            isFirst || value
                                                ? validateEmail(value)
                                                : true,
                                    }}
                                    render={({ field: f, fieldState }) => (
                                        <TextField
                                            {...f}
                                            label={`Email${isFirst ? ' *' : ''}`}
                                            type="email"
                                            fullWidth
                                            sx={{
                                                ...textFieldSx,
                                                gridColumn: { sm: '1 / -1' },
                                            }}
                                            error={Boolean(fieldState.error)}
                                            helperText={fieldState.error?.message}
                                        />
                                    )}
                                />
                                <PhoneField
                                    name={`contacts.${index}.phone`}
                                    label="Phone"
                                    required={isFirst}
                                    sx={{ gridColumn: { sm: '1 / -1' } }}
                                />
                            </FieldGrid>

                            <Controller
                                control={control}
                                name={`contacts.${index}.sendInvoice` as const}
                                render={({ field: f }) => (
                                    <FormControlLabel
                                        sx={{ mt: 1 }}
                                        control={
                                            <Switch
                                                checked={Boolean(f.value)}
                                                onChange={(event) =>
                                                    f.onChange(event.target.checked)
                                                }
                                                sx={{
                                                    '& .Mui-checked': {
                                                        color: BRAND_CORAL,
                                                    },
                                                    '& .Mui-checked + .MuiSwitch-track':
                                                        {
                                                            backgroundColor:
                                                                BRAND_CORAL,
                                                        },
                                                }}
                                            />
                                        }
                                        label="Send invoices to this contact"
                                    />
                                )}
                            />
                        </Box>
                    )
                })}
            </Stack>

            <Button
                onClick={() => append(emptyContact())}
                startIcon={<AddIcon />}
                sx={{
                    mt: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    color: BRAND_CORAL,
                }}
            >
                Add another contact
            </Button>
        </Box>
    )
}

export default ContactsStep
